import React, { useEffect, useState } from 'react';
import { FloppyDisk } from '@phosphor-icons/react';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const MODES = ['Flight', 'Train', 'Car', 'Bus', 'Other'];

const toDateInput = (value) => {
  if (!value) return '';
  const directDate = String(value).split('T')[0];
  return /^\d{4}-\d{2}-\d{2}$/.test(directDate) ? directDate : '';
};

export default function ManualTravelDetailsDialog({
  open,
  onOpenChange,
  eventId,
  guest,
  ticketType = 'arrival',
  onSaved,
}) {
  const [mode, setMode] = useState('flight');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [details, setDetails] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const info = guest?.arrival_info || {};
    setMode(info[`${ticketType}_mode`] || 'flight');
    setDate(toDateInput(info[`${ticketType}_date`]));
    setTime(info[`${ticketType}_time`] || '');
    setDetails(info[`${ticketType}_details`] || '');
  }, [guest, open, ticketType]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!eventId || !guest?.id) return;

    const info = guest.arrival_info || {};
    const nextInfo = {
      ...info,
      [`${ticketType}_mode`]: mode,
      [`${ticketType}_date`]: date ? new Date(`${date}T00:00:00`).toISOString() : null,
      [`${ticketType}_time`]: time,
      [`${ticketType}_details`]: details,
      ticket_uploads: info.ticket_uploads || [],
      arrival_tickets: info.arrival_tickets || [],
      departure_tickets: info.departure_tickets || [],
    };

    try {
      setSaving(true);
      const response = await api.put(`/events/${eventId}/guests/${guest.id}`, {
        arrival_info: nextInfo,
      });
      toast.success(`${ticketType === 'arrival' ? 'Arrival' : 'Departure'} details saved`);
      onOpenChange(false);
      await onSaved?.(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save travel details');
    } finally {
      setSaving(false);
    }
  };

  const label = ticketType === 'arrival' ? 'Arrival' : 'Departure';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit} className="space-y-5">
          <DialogHeader>
            <DialogTitle>Enter {label.toLowerCase()} details manually</DialogTitle>
            <DialogDescription>
              Ticket reading requires a subscription. Add the travel information below instead.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Mode of travel</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MODES.map((item) => (
                    <SelectItem key={item} value={item} className="capitalize">
                      {item}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor={`manual-${ticketType}-date`}>Date</Label>
                <Input
                  id={`manual-${ticketType}-date`}
                  type="date"
                  required
                  value={date}
                  onChange={(event) => setDate(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor={`manual-${ticketType}-time`}>Time</Label>
                <Input
                  id={`manual-${ticketType}-time`}
                  type="time"
                  value={time}
                  onChange={(event) => setTime(event.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor={`manual-${ticketType}-details`}>Flight, train, or vehicle details</Label>
              <Input
                id={`manual-${ticketType}-details`}
                value={details}
                onChange={(event) => setDetails(event.target.value)}
                placeholder="e.g., flight 6E 204"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="bg-wedding-sage hover:bg-wedding-sage/90">
              <FloppyDisk className="h-4 w-4" />
              {saving ? 'Saving...' : 'Save details'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
